import { isWorkflowOverviewGroup, isWorkflowOverviewItem } from '../predicates'
import type { WorkflowReviewStatusData } from '@app/shared/types/sf-data'
import type {
  OverviewChecklist,
  PreparedWorkflowOverviewAccountEntry,
  PreparedWorkflowOverviewReconciliationText,
  WorkflowOverviewGroup,
  WorkflowOverviewItem,
  WorkflowOverviewItemType,
} from '@app/application/workflow-overview-navigation/types'
import { isNotEmptyTree } from '@app/utils/predicates'

import fuzzysort from 'fuzzysort'
import { defineStore, storeToRefs } from 'pinia'
import { computed, ref } from 'vue'

import {
  exposeTopSingleParent,
  groupReviewStatus,
  prepareSearchWorkflowItem,
} from './utils'
import { SfLog } from '@app/adapters/logger'
import {
  getChecklists,
  getOverview,
} from '@app/application/workflow-overview-navigation/api'
import TreeNode from '@app/utils/tree'

import { useWorkflowOverviewSearch } from './search'
import { useWorkflowReviewItemStatus } from '@app/application/workflows/screens/workflow-review/store/pinia/workflow-review-item-status'
import { useWorkflowProgress } from './workflow-progress'
import { collapsedGroupsStorageKey } from '@app/application/workflows/constants'
import type { WorkflowProgressResponse } from '@app/application/workflows/types'

type OverviewParams = {
  filter?: string
  isPulseUser?: boolean
  highlightedItemId?: number
  highlightedItemType?: WorkflowOverviewItemType
  includeReviewStatus: boolean
  ledgerId: number
  workflowId: number
}

const SYNTHETIC_ROOT_ID = 1
export const useWorkflowOverview = defineStore('workflow-overview', () => {
  const reviewStatusStore = useWorkflowReviewItemStatus()
  const searchStore = useWorkflowOverviewSearch()

  const { reviewItemStatus } = storeToRefs(reviewStatusStore)
  const { searchTerm } = storeToRefs(searchStore)

  const overviewTree = ref<
    TreeNode<WorkflowOverviewGroup | WorkflowOverviewItem>
  >(
    TreeNode.of(
      {
        id: SYNTHETIC_ROOT_ID,
        name: 'root',
        type: 'group',
        children: [],
      },
      []
    )
  )

  const starredItemStatus = ref<Record<string, boolean>>({})

  const checklists = ref<OverviewChecklist[]>([])

  const overviewArray = computed(() => {
    return overviewTree.value.unwrapDeep(false)
  })

  const overviewTreeData = computed(() => {
    if (hasAnyFilterApplied.value) {
      return filteredTreeData.value
    }

    return overviewArray.value
  })

  const expandedOverviewGroups = ref<Record<string, boolean>>({})
  const workflowId = ref<number | null>(null)
  const reportId = ref<number | null>(null)

  const hasAnyFilterApplied = computed(() => {
    return searchTerm.value !== ''
  })

  const isOverviewTreeEmpty = computed(() => {
    return overviewTree.value.children.length === 0
  })

  const itemAndGroupReviewsById = computed(() => {
    const accumulator = {} as Record<
      string,
      Pick<WorkflowReviewStatusData, 'review' | 'reviews'>
    >

    for (const treeItem of overviewTree.value.reverseIterator()) {
      const item = treeItem.unwrap()

      if (!item) {
        continue
      }

      if (item.type === 'group') {
        const reviewResult = groupReviewStatus(
          item,
          reviewItemStatus.value,
          accumulator
        )

        if (reviewResult.reviews.length > 0 || reviewResult.review) {
          accumulator[item.id] = reviewResult
        }
      } else {
        const key =
          item.type === 'reconciliation_text' ? item.id : item.accountEntryId

        if (item.type && key) {
          const itemStatus = reviewItemStatus.value[`${item.type}:${key}`]

          if (itemStatus?.reviews?.length > 0 || itemStatus?.review) {
            accumulator[key] = itemStatus
          }
        }
      }
    }

    return accumulator
  })

  const preparedSearchCollection = computed(() => {
    const collection = [] as Array<
      | PreparedWorkflowOverviewAccountEntry
      | PreparedWorkflowOverviewReconciliationText
    >

    if (overviewTree.value) {
      for (const item of overviewTree.value) {
        const rawItem = item.unwrap()

        if (!isWorkflowOverviewGroup(rawItem)) {
          const preparedItem = prepareSearchWorkflowItem(rawItem)

          if (preparedItem) {
            collection.push(preparedItem)
          }
        }
      }
    }

    return collection
  })

  const searchResults = computed(() => {
    const fuzzyResults = fuzzysort.go(
      searchTerm.value,
      preparedSearchCollection.value,
      {
        keys: ['preparedName', 'preparedNumber', 'preparedOriginalNumber'],
        threshold: -1000,
      }
    )

    return fuzzyResults.reduce((acc, result) => {
      const item = { ...result }
      acc[item.obj.id] = true

      return acc
    }, {} as Record<number, boolean>)
  })

  const filteredTree = computed(() => {
    const results = searchResults.value

    const filteredTree = overviewTree.value.filter((item) => {
      if (isWorkflowOverviewGroup(item)) {
        return true
      }

      return !!results[item.id]
    })

    return filteredTree
  })

  const filteredElementsById = computed(() => {
    const accumulator = {
      account_entry: {} as Record<number, boolean>,
      reconciliation_text: {} as Record<number, boolean>,
      filteredIndeces: [] as Record<number, number>,
      groups: {} as Record<number, boolean>,
    }

    if (
      !filteredTree.value ||
      !isNotEmptyTree(filteredTree.value) ||
      !searchTerm.value
    ) {
      return accumulator
    }

    let itemIndex = 0

    for (const item of filteredTree.value) {
      const unwrapedItem = item.unwrap()

      if (isWorkflowOverviewGroup(unwrapedItem)) {
        accumulator.groups[unwrapedItem.id] = true
      } else {
        if (unwrapedItem.type === 'reconciliation_text') {
          accumulator.reconciliation_text[unwrapedItem.id] = true
          accumulator.filteredIndeces[unwrapedItem.reviewIndex] = itemIndex++
        } else {
          if (unwrapedItem.accountEntryId) {
            accumulator.account_entry[unwrapedItem.accountEntryId] = true
            accumulator.filteredIndeces[unwrapedItem.reviewIndex] = itemIndex++
          }
        }
      }
    }

    return accumulator
  })

  const filteredGroupsById = computed(() => {
    return filteredElementsById.value.groups
  })

  const filteredReviewIndeces = computed(() => {
    return filteredElementsById.value.filteredIndeces
  })

  const filteredTreeData = computed(() => {
    return filteredTree.value && isNotEmptyTree(filteredTree.value)
      ? filteredTree.value.unwrapDeep(false)
      : []
  })

  function getPersistedStorageKey(): string | null {
    if (workflowId.value === null || reportId.value === null) return null
    return collapsedGroupsStorageKey(workflowId.value, reportId.value)
  }

  function readPersistedCollapsedIds(): Set<number> | null {
    const key = getPersistedStorageKey()
    if (!key) return null
    const raw = localStorage.getItem(key)
    if (raw === null) return null
    try {
      const parsed: unknown = JSON.parse(raw)
      if (!Array.isArray(parsed)) return null
      const numericIds = parsed.filter(
        (id): id is number => typeof id === 'number'
      )
      return new Set(numericIds)
    } catch (e) {
      SfLog.warn('Failed to parse persisted collapsed group ids', {
        extra: { error: e },
      })
      return null
    }
  }

  function persistCollapsedGroups(): void {
    const key = getPersistedStorageKey()
    if (!key) return
    const collapsedIds: number[] = []
    for (const treeItem of overviewTree.value) {
      const item = treeItem.unwrap()
      if (!isWorkflowOverviewGroup(item)) continue
      if (item.id === SYNTHETIC_ROOT_ID) continue
      if (!expandedOverviewGroups.value[item.id]) {
        collapsedIds.push(item.id)
      }
    }
    localStorage.setItem(key, JSON.stringify(collapsedIds))
  }

  function applyInitialExpandState(): void {
    const persistedCollapsed = readPersistedCollapsedIds()
    for (const treeItem of overviewTree.value) {
      const item = treeItem.unwrap()
      if (!isWorkflowOverviewGroup(item)) continue
      const shouldExpand = persistedCollapsed
        ? !persistedCollapsed.has(item.id)
        : item.collapsed !== true
      if (shouldExpand) {
        expandOverviewGroup(item.id.toString())
      }
    }
  }

  async function fetchOverview(params: OverviewParams): Promise<void> {
    try {
      const {
        filter = '',
        includeReviewStatus,
        isPulseUser,
        highlightedItemId,
        highlightedItemType,
        ledgerId,
        workflowId: workflowIdArg,
      } = params

      const response = await getOverview({
        filter,
        includeReviewStatus,
        highlightedItemId,
        highlightedItemType,
        ledgerId,
        workflowId: workflowIdArg,
      })

      if (!response) {
        return
      }

      const {
        tree,
        cachedWorkflowProgressData,
        reportId: responseReportId,
      } = response

      if (!tree || tree.length === 0) {
        return
      }

      workflowId.value = workflowIdArg
      reportId.value = responseReportId

      if (cachedWorkflowProgressData && !isPulseUser) {
        try {
          const parsedData = JSON.parse(
            cachedWorkflowProgressData
          ) as WorkflowProgressResponse
          const workflowProgressStore = useWorkflowProgress()
          workflowProgressStore.receiveWorkflowProgress(parsedData)
        } catch (error) {
          SfLog.error('Failed to parse cached workflow progress data', {
            extra: { error },
          })
        }
      }

      const singleParent = exposeTopSingleParent(tree)
      const root = TreeNode.of<WorkflowOverviewGroup | WorkflowOverviewItem>(
        {
          id: SYNTHETIC_ROOT_ID,
          name: 'root',
          type: 'group',
          children: [],
        },
        tree
      )
        .map((value) => {
          if (isWorkflowOverviewGroup(value)) {
            return Object.freeze(value)
          } else if (isWorkflowOverviewItem(value)) {
            const effectivePath = isPulseUser ? value.proposalPath : value.path

            return Object.freeze({
              ...value,
              path: effectivePath,
            })
          }

          return value
        })
        .filter((item) => {
          if (isWorkflowOverviewGroup(item)) {
            return item.name === 'root' || item.children.length > 0
          } else {
            if (!item?.id || !item?.type) {
              return false
            }

            return true
          }
        })

      if (isNotEmptyTree(root)) {
        overviewTree.value = root
        extractStarredStatus(tree)
      }

      if (singleParent !== -1) {
        const persisted = readPersistedCollapsedIds()
        if (!persisted?.has(singleParent)) {
          expandOverviewGroup(singleParent.toString())
        }
      }

      if (includeReviewStatus) {
        // Seed the review status store from the overview tree so every item's
        // reviewed indicator is available on mount, independent of the lazy
        // Liquid batches.
        tree.forEach(extractReviewData)
      }
    } catch (e) {
      SfLog.warn('Error fetching overview', {
        extra: {
          error: e,
        },
      })
    }
  }

  async function fetchChecklists({
    ledgerId,
    workflowId,
  }: {
    ledgerId: number
    workflowId: number
  }): Promise<void> {
    try {
      checklists.value = (await getChecklists({ ledgerId, workflowId })) ?? []
    } catch (e) {
      SfLog.warn('Error fetching checklists', {
        extra: {
          error: e,
        },
      })
    }
  }

  function getStarredItemKey(item: WorkflowOverviewItem): string | null {
    if (item.type === 'account_entry') {
      // use key as account:<id> to be consistent with wf ProgressJob response
      // as it only contains account data
      return `account:${item.id}`
    } else if (item.type === 'reconciliation_text') {
      return `reconciliation_text:${item.id}`
    }
    return null
  }

  function traverseStarredItems(
    item: WorkflowOverviewItem | WorkflowOverviewGroup,
    starredStatus: Record<string, boolean>
  ): void {
    if (item.type === 'group') {
      item.children.forEach((child) =>
        traverseStarredItems(child, starredStatus)
      )
    } else {
      const key = getStarredItemKey(item)
      if (key) {
        starredStatus[key] = item.starred
      }
    }
  }

  function extractStarredStatus(
    items: Array<WorkflowOverviewItem | WorkflowOverviewGroup>
  ): void {
    const newStarredStatus: Record<string, boolean> = {}
    items.forEach((item) => traverseStarredItems(item, newStarredStatus))
    starredItemStatus.value = newStarredStatus
  }

  function extractReviewData(
    item: WorkflowOverviewItem | WorkflowOverviewGroup
  ): void {
    if (item.type === 'group') {
      item.children.forEach(extractReviewData)
    } else {
      if (item.workflowReviewStatusData && item.type) {
        reviewStatusStore.updateReviewItemStatus({
          ...item.workflowReviewStatusData,
          itemClass: item.type,
          parentId:
            item.type === 'account_entry'
              ? item.accountEntryId
                ? item.accountEntryId
                : item.id
              : item.id,
        })
      }
    }
  }

  function expandOverviewGroup(groupId: string): void {
    expandedOverviewGroups.value = {
      ...expandedOverviewGroups.value,
      [groupId]: true,
    }
  }

  function expandItemParents(
    itemId: number,
    type: WorkflowOverviewItemType | 'group'
  ): void {
    const item = overviewTree.value.find((i) => {
      if (!isWorkflowOverviewGroup(i)) {
        if (i.type === 'account_entry') {
          return i.accountEntryId === itemId && i.type === type
        } else {
          return i.id === itemId && i.type === type
        }
      }

      return i.id === itemId
    })

    if (!item || !item?.parent || !isNotEmptyTree(item)) {
      return
    }

    const parentId = item.parent.unwrap().id

    expandOverviewGroup(parentId.toString())
    expandItemParents(parentId, 'group')
  }

  function toggleOverviewGroup(groupId: string): void {
    expandedOverviewGroups.value = {
      ...expandedOverviewGroups.value,
      [groupId]: !expandedOverviewGroups.value[groupId],
    }
    persistCollapsedGroups()
  }

  function updateItemStarredStatus(
    itemType: string,
    itemId: number,
    starred: boolean
  ): void {
    const key = `${itemType}:${itemId}`
    starredItemStatus.value[key] = starred
  }

  return {
    expandedOverviewGroups,
    checklists,
    fetchChecklists,
    fetchOverview,
    filteredElementsById,
    filteredGroupsById,
    filteredReviewIndeces,
    hasAnyFilterApplied,
    expandOverviewGroup,
    expandItemParents,
    isOverviewTreeEmpty,
    itemAndGroupReviewsById,
    overviewTreeData,
    starredItemStatus,
    toggleOverviewGroup,
    applyInitialExpandState,
    updateItemStarredStatus,
    getStarredItemKey,
  }
})
